import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as forge from 'node-forge';
import { environment } from 'src/environments/environment';
import { firstValueFrom } from 'rxjs';
import { ApiResponse } from '../../interfaces/api/api-response.interface';

const STORAGE_KEY = 'aes-gcm-encryption-pub-key';
@Injectable({ providedIn: 'root' })
export class EncryptionService {
  public readonly PUBLIC_KEY_URL = `${environment.apiUrl}/auth/public-key`;
  private readonly EXPECTED_FINGERPRINT = environment.PUBLIC_KEY_FINGERPRINT;
  private readonly MAX_CACHE_AGE = 1000 * 60 * 60 * 24;

  private readonly AAD_TEXT = environment.AES_GCM_AAD ?? '';
  private readonly ENVELOPE_VERSION = '1.0';

  private publicKey!: forge.pki.rsa.PublicKey;

  constructor(private http: HttpClient) {}

  async loadPublicKey(): Promise<void> {
    try {
      const cached = localStorage.getItem(STORAGE_KEY);
      if (cached) {
        return this.loadPublicKeySync();
      }
      const resp = await firstValueFrom(
        this.http.get<ApiResponse<string>>(this.PUBLIC_KEY_URL)
      );
      const pem = resp.data.trim();

      const md = forge.md.sha256.create();
      md.update(pem, 'utf8');
      const actualFingerprintHex = this.calculateFingerprintHex(pem);
      if (
        !this.EXPECTED_FINGERPRINT ||
        this.normalizeHex(actualFingerprintHex) !==
          this.normalizeHex(this.EXPECTED_FINGERPRINT)
      ) {
        throw new Error('Invalid public key');
      }

      this.publicKey = forge.pki.publicKeyFromPem(pem);

      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          pem,
          timestamp: Date.now(),
        })
      );
    } catch (err) {
      console.log(err);
    }
  }

  loadPublicKeySync(): void {
    const cached = localStorage.getItem(STORAGE_KEY);
    const parsed = JSON.parse(cached!);
    const age = Date.now() - parsed.timestamp;

    if (age < this.MAX_CACHE_AGE) {
      const pemLocal = parsed.pem.trim();
      const fingerprintLocal = this.calculateFingerprintHex(pemLocal);
      if (
        this.normalizeHex(fingerprintLocal) ===
        this.normalizeHex(this.EXPECTED_FINGERPRINT)
      ) {
        this.publicKey = forge.pki.publicKeyFromPem(pemLocal);
        return;
      }
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }
  encrypt(plaintext: string): string {
    if (!this.publicKey) {
      this.loadPublicKeySync();
    }

    const aesKeyBytes = new Uint8Array(32);
    const ivBytes = new Uint8Array(12);
    crypto.getRandomValues(aesKeyBytes);
    crypto.getRandomValues(ivBytes);

    const aesKeyForge = forge.util.binary.raw.encode(aesKeyBytes);
    const ivForge = forge.util.binary.raw.encode(ivBytes);

    let aadForge: string | undefined;
    if (this.AAD_TEXT && this.AAD_TEXT.length > 0) {
      aadForge = forge.util.encodeUtf8(this.AAD_TEXT);
    }

    const cipher = forge.cipher.createCipher('AES-GCM', aesKeyForge);
    if (aadForge) {
      cipher.start({ iv: ivForge, additionalData: aadForge, tagLength: 128 });
    } else {
      cipher.start({ iv: ivForge, tagLength: 128 });
    }
    cipher.update(forge.util.createBuffer(plaintext, 'utf8'));
    const success = cipher.finish();
    if (!success) {
      throw new Error('Error interno de cifrado AES-GCM.');
    }

    const ctBytes = cipher.output.getBytes();
    const tagBytes = cipher.mode.tag.getBytes();

    const wrappedKeyBytes = this.publicKey.encrypt(aesKeyForge, 'RSA-OAEP', {
      md: forge.md.sha256.create(),
      mgf1: forge.mgf.mgf1.create(forge.md.sha256.create()),
    });

    const toBase64 = (b: string) => forge.util.encode64(b);

    const envelope = {
      version: this.ENVELOPE_VERSION,
      wrappedKey: toBase64(wrappedKeyBytes),
      iv: toBase64(ivForge),
      ct: toBase64(ctBytes),
      tag: toBase64(tagBytes),
    };

    return JSON.stringify(envelope);
  }

  encryptWithDate(data: string): string {
    const timestamp = Date.now().toString();
    const toEncrypt = `${data}:${timestamp}`;
    return this.encrypt(toEncrypt);
  }

  private calculateFingerprintHex(pem: string): string {
    const body = pem
      .replace(/-----BEGIN PUBLIC KEY-----/g, '')
      .replace(/-----END PUBLIC KEY-----/g, '')
      .replace(/\r?\n|\r/g, '');

    const der = forge.util.decode64(body);
    const md = forge.md.sha256.create();
    md.update(der, 'raw');
    return md.digest().toHex();
  }

  private normalizeHex(hex: string): string {
    return hex.replace(/\s+/g, '').toLowerCase();
  }
}
